import React, { ReactNode } from "react"
import {
  Box, Flex, HStack, Stack, Text,
} from "@chakra-ui/react"
import { Link as RouterLink } from "react-router-dom"
import Button from "components/Buttons/Button"
import Link from "components/elements/Link"
import LinkField from "components/elements/LinkField"
import LanguageTemplatesIcon from "components/elements/LanguageTemplatesIcon"
import PageContent from "components/layout/PageContent"
import { useLanguageTemplates } from "queries/languageTemplates"
import useModal from "services/useModal"
import usePermissions from "services/permissions"
import { DioboxEvent, ModalName } from "sharedTypes"
import {
  GOOGLE_ANALYTICS_INTEGRATION_ARTICLE_LINK,
  GUEST_PORTAL_HELP_ARTICLE_LINK,
} from "utilities/externalLinks"
import { eventSettingsPath } from "utilities/routes"
import TitleWithSubtitle from "../TitleWithSubtitle"

type SectionProps = {
  title: string
  aside?: ReactNode
  action?: ReactNode
  children: ReactNode
}

const Section = ({
  title, aside, action, children,
}: SectionProps) => (
  <Stack spacing={5} p={6} bg="white" borderWidth={1} borderRadius="xl">
    <Flex justify="space-between" align="center" gap={4}>
      <Text fontSize="lg" fontWeight="semibold">{title}</Text>
      {aside}
    </Flex>
    {children}
    {action && <Box textAlign="center">{action}</Box>}
  </Stack>
)

const PortalOverview = ({ event }: { event: DioboxEvent }) => {
  const showModal = useModal()
  const { data: languageTemplates } = useLanguageTemplates(event.id)
  const { canViewSettings } = usePermissions()
  const defaultLanguage = languageTemplates
    && (languageTemplates.find(({ id }) => id === event.defaultLanguageTemplateId)?.name ?? "English")

  return (
    <PageContent noPadding>
      <TitleWithSubtitle title="Portal Overview" />
      <Stack spacing={6}>
        <Stack spacing={4}>
          <Text fontSize="lg">
            Your Event Portal brings together the pages guests use to view your forms, register,
            see their confirmation, and manage their profile and communication preferences.
          </Text>
          <Text fontSize="lg">
            Learn more about the{" "}
            <Link href={GUEST_PORTAL_HELP_ARTICLE_LINK} isExternal>Event Portal</Link>.
          </Text>
        </Stack>
        <Section
          title="Portal Link"
          action={(
            <Button variant="outline" onClick={() => showModal(ModalName.EventLink)}>
              Event Link Settings
            </Button>
          )}
        >
          <Text>Share this link so guests can see all public forms for your event.</Text>
          {event.urls?.portal && <LinkField url={event.urls.portal} />}
        </Section>
        <Section
          title="Analytics"
          action={(
            <Button variant="outline" onClick={() => showModal(ModalName.GoogleAnalytics)}>
              Set up Google Analytics
            </Button>
          )}
        >
          <Text>
            Connect <strong>Google Analytics</strong> for more detailed information about page
            visits and guest activity. To learn more, see{" "}
            <Link href={GOOGLE_ANALYTICS_INTEGRATION_ARTICLE_LINK} isExternal>
              Google Analytics Help
            </Link>.
          </Text>
        </Section>
        <Section
          title="Language"
          aside={defaultLanguage && (
            <HStack spacing={2} color="#7ED321" fontWeight="semibold">
              <Text as="span">{defaultLanguage}</Text>
              <LanguageTemplatesIcon boxSize={6} />
            </HStack>
          )}
          action={canViewSettings && (
            <Button
              variant="outline"
              as={RouterLink}
              to={eventSettingsPath(event.id, "language-templates")}
            >
              Manage Language Templates
            </Button>
          )}
        >
          <Text>
            Use <strong>Language Templates</strong> to show the Portal in another language,
            translate individual forms for multilingual events, or create your own template.
          </Text>
        </Section>
      </Stack>
    </PageContent>
  )
}

export default PortalOverview
